import type { HubConnection } from '@microsoft/signalr';
import { getHttpConnection } from './SignalR.internals';

const SIGNALR_PING_TYPE = 6;
const SIGNALR_ERROR_TYPE = 7;

const parseSignalRMessage = (
  data: string,
): { type?: number; error?: string; message?: string } | null => {
  try {
    return JSON.parse(data) as {
      type?: number;
      error?: string;
      message?: string;
    };
  } catch {
    return null;
  }
};

/** Intercepts inbound messages at the HttpConnection layer for debugging. */
export const setupConnectionReceiveInterceptor = (
  hubName: string,
  connection: HubConnection,
): void => {
  try {
    const httpConnection = getHttpConnection(connection);
    if (!httpConnection) {
      return;
    }

    const originalOnReceive = httpConnection.onreceive;
    httpConnection.onreceive = (data: string | ArrayBuffer) => {
      try {
        if (typeof data === 'string') {
          if (data !== '{}' && !data.includes('"type":6')) {
            console.log(`📨 [${hubName}] Connection received:`, data);
          }

          const parsed = parseSignalRMessage(data);
          if (parsed?.type === SIGNALR_PING_TYPE) {
            console.log(`💓 [${hubName}] Keepalive ping/pong received`);
          } else if (parsed?.type === SIGNALR_ERROR_TYPE) {
            console.error(
              `🚨 [${hubName}] Server error (type 7):`,
              parsed.error || parsed.message || JSON.stringify(parsed),
            );
          } else if (parsed?.type !== undefined) {
            console.log(
              `📨 [${hubName}] Message type ${parsed.type}:`,
              parsed,
            );
          }
        }
      } catch (error) {
        console.warn('⚠️ Error intercepting connection message:', error);
      }

      if (originalOnReceive) {
        originalOnReceive.call(httpConnection, data);
      }
    };
  } catch (error) {
    console.warn(
      `⚠️ Could not set up connection interception for [${hubName}]:`,
      error,
    );
  }
};

/** Stores WebSocket reference and logs outbound ping frames. */
export const setupWebSocketInterceptor = (
  hubName: string,
  socket: WebSocket,
  onClose: () => void,
): void => {
  const originalSend = socket.send.bind(socket);
  socket.send = function sendWithLogging(data: string | ArrayBuffer | Blob) {
    if (typeof data === 'string') {
      const parsed = parseSignalRMessage(data);
      if (parsed?.type === SIGNALR_PING_TYPE) {
        console.log(`💓 [${hubName}] Keepalive ping SENT:`, data);
      }
    }
    return originalSend(data);
  };

  const originalOnClose = socket.onclose;
  socket.onclose = (event: CloseEvent) => {
    console.error(`🔴 [${hubName}] WebSocket closed:`, {
      code: event.code,
      reason: event.reason,
      wasClean: event.wasClean,
    });
    onClose();
    if (originalOnClose) {
      originalOnClose.call(socket, event);
    }
  };
};
